import React, { useState, useEffect, useRef } from "react";
import { Form, Button } from "react-bootstrap";
import { Eye, EyeOff } from "lucide-react";
import OTPInput from "./OTPInput";
import { loginUser, verifyAccount } from "../../requests";
import {
  getFormattedEmail,
  notifyError,
  notifySuccess,
  saveAuthToken,
} from "../../utils/helper";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [otpPage, setOtpPage] = useState(false);
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const Navigate = useNavigate();
  const dispatch = useDispatch();
  const [timer, setTimer] = useState(0);
  const timerRef = useRef(null);

  // Start timer when otpPage becomes true
  useEffect(() => {
    if (otpPage) {
      setTimer(60);
    } else {
      setTimer(0);
    }
  }, [otpPage]);

  // Timer countdown effect
  useEffect(() => {
    if (timer > 0) {
      timerRef.current = setTimeout(() => setTimer(timer - 1), 1000);
    } else {
      clearTimeout(timerRef.current);
    }
    return () => clearTimeout(timerRef.current);
  }, [timer]);

  const handleSubmit = async (e) => {
    dispatch(startLoader())
    e.preventDefault();
    if (otpPage) {
      if (otp) {
        await handleAccountVerification();
      }
    } else {
      if (validate()) {
        await handleAccountLogin(1);
        // Proceed with form submission
      }
    }
    dispatch(stopLoader())
  };

  const handleAccountLogin = async (newOtp = 0) => {
    try {
      const response = await loginUser({
        email: email,
        password: password,
        newOtp: newOtp
      });
      if (response.status === 201) {
        setOtpPage(true);
      }
    } catch (error) {
      console.log(error);
      if (error.response && error.response.status === 401) {
        notifyError(error.response.data.message);
        // dispatch(setUser(null));
        // navigate("/login");
      } else if (error.response) {
        notifyError(error.response.data.message);
      } else {
        notifyError("Something went wrong");
      }
    }
  };

  const handleAccountVerification = async () => {
    try {
      const response = await verifyAccount({
        email: email,
        otp: otp.join(""),
      });
      if (response.status === 200) {
        notifySuccess(response.data.message);
        saveAuthToken(response.data.token);
        Navigate("/");
      }
    } catch (error) {
      console.log(error);
      if (error.response && error.response.status === 401) {
        notifyError(error.response.data.message);
        // dispatch(setUser(null));
        // navigate("/login");
      } else if (error.response) {
        notifyError(error.response.data.message);
      } else {
        notifyError("Something went wrong");
      }
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    if (otpPage) {
      if (timer === 0) {
        await handleAccountLogin(1)
        notifySuccess("New OTP sent to your email.");
        setTimer(60); // Restart timer
      }
    } else {
      Navigate("/forgot-password");
    }
  }

  const validate = () => {
    const newErrors = {};

    // Email validation
    if (!email) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Enter a valid email address.";
    }

    // Password validation
    if (!password) {
      newErrors.password = "Password is required.";
    }
    // else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\W).{8,}/.test(password)) {
    //   newErrors.password =
    //     "Password must be at least 8 characters long, contain a lowercase letter, an uppercase letter, and at least one special character.";
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <div className="login-container">
      <h6 className="mb-3">PractAIce</h6>
      <h4 className="mb-4 font-clash-display fw-600">
        {otpPage ? "Enter Verification Code" : "Login to your account"}
      </h4>
      {otpPage && (
        <p style={{ marginTop: "-1rem" }} className="mb-4">
          Code sent to {getFormattedEmail(email)}
        </p>
      )}
      <Form onSubmit={handleSubmit}>
        {otpPage ? (
          <OTPInput otp={otp} setOtp={setOtp} />
        ) : (
          <>
            <Form.Group className="mb-3" controlId="formEmail">
              <Form.Label>Work email</Form.Label>
              <Form.Control
                type="email"
                placeholder="john.dowry@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                isInvalid={!!errors.email}
                style={{ backgroundColor: "white" }}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formPassword">
              <Form.Label>Password</Form.Label>
              <div className="input-group">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  isInvalid={!!errors.password}
                />
                <Button
                  variant=""
                  onClick={() => setShowPassword(!showPassword)}
                  className="show-password-btn"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </Button>
                <Form.Control.Feedback type="invalid">
                  {errors.password}
                </Form.Control.Feedback>
              </div>
            </Form.Group>
          </>
        )}
        <Form.Text className="text-muted mb-3">
          {otpPage ? (
            <button
              onClick={handleForgotPassword}
              className="forgot-password btn"
              href="#forgot-password"
              disabled={timer > 0}
              style={{ cursor: timer > 0 ? "not-allowed" : "pointer", opacity: timer > 0 ? 0.6 : 1 }}
            >
              {timer > 0 ? `Send Again? Please wait (${timer}s)` : "Send Again?"}
            </button>
          ) : (
            <button
              onClick={handleForgotPassword}
              className="forgot-password btn"
              href="#forgot-password"
            >
              Forgot Password?
            </button>
          )}
        </Form.Text>

        <Button
          variant="primary"
          type="submit"
          className="w-100 mt-4 login-submit-btn"
        >
          {otpPage ? "Login" : "Continue"}
        </Button>
      </Form>
    </div>
  );
};

export default Login;
